import SaveIcon from '@mui/icons-material/Save'
import { Box, Paper, Typography, Divider, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { SectionTitle } from '~/src/components/molecules'
import { LoadingNormal } from '~/src/components/organisms'
import { SpecialOfferForm } from '~/src/components/pages/SpecialOffer/components/SpecialOfferForm'
import { ProtectedPage } from '~/src/components/templates/ProtectedPage/ProtectedPage'
import { useMessage } from '~/src/hooks'
import { otherRepository } from '~/src/repositories'
import { DownloadsFileItem, IDownloadsBody } from '~/src/repositories/Other/models/Other'
import { FileUploadStateProps } from '~/src/types/form'
import LoadingButton from '@mui/lab/LoadingButton'

/**
 * SpecialOffer
 */
export const SpecialOffer: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const { handleOpenWithSuccessMessage, handleOpenWithErrorMessage } = useMessage()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [engineFile, setEngineFile] = useState<FileUploadStateProps>(undefined)
  const [ybFile, setYbFile] = useState<FileUploadStateProps>(undefined)

  const { data, error, mutate } = useSWR(['/special-offer'], () => otherRepository.getDownloads())

  useEffect(() => {
    if (error) handleOpenWithErrorMessage(error.message)
  }, [error])

  /**
   * handleSubmit
   * @param e
   */
  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!engineFile && !ybFile) {
      handleOpenWithErrorMessage(t('special_offer.validate_error_message'))
      return
    }
    const uploadData: DownloadsFileItem[] = []

    if (engineFile) {
      const engineData = {
        file: {
          filename: engineFile.filename,
          path: engineFile.url,
        },
        type: 1,
      }
      uploadData.push(engineData as DownloadsFileItem)
    }
    if (ybFile) {
      const ybData = {
        file: {
          filename: ybFile.filename,
          path: ybFile.url,
        },
        type: 2,
      }
      uploadData.push(ybData as DownloadsFileItem)
    }

    if (uploadData.length === 0) {
      handleOpenWithErrorMessage(t('special_offer.validate_error_message'))
      return
    }

    const body: IDownloadsBody = {
      files: uploadData,
    }
    setIsLoading(true)
    try {
      await otherRepository.patchDownloads(body)
      await mutate()
      setEngineFile(undefined)
      setYbFile(undefined)
      handleOpenWithSuccessMessage(t('special_offer.success_message'))
    } catch (e) {
      handleOpenWithErrorMessage(t('special_offer.error_message'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ProtectedPage pageTitle={t('new_stock_list.new_stock_list')}>
      {!data ? (
        <LoadingNormal />
      ) : (
        <Paper>
          <form onSubmit={handleSubmit}>
            <Box mb={4}>
              <SectionTitle
                customStyles={{
                  color: '#000000',
                  boxShadow: 'none',
                  padding: '16px',
                }}
              >
                {t('special_offer.latest_stock_engine_list')}
              </SectionTitle>
              <Divider />
              <SpecialOfferForm setUploadFile={setEngineFile} fileData={data.engine} />
            </Box>
            <Box mb={7}>
              <SectionTitle
                customStyles={{
                  color: '#000000',
                  boxShadow: 'none',
                  padding: '16px',
                }}
              >
                {t('special_offer.yb_vehicle_new_arrival_list')}
              </SectionTitle>
              <Divider />
              <SpecialOfferForm setUploadFile={setYbFile} fileData={data.yb} />
            </Box>

            <Divider />

            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} p={'16px'}>
              <LoadingButton
                color="primary"
                type="submit"
                loading={isLoading}
                loadingPosition="start"
                startIcon={<SaveIcon sx={{ width: '24px', height: '24px' }} />}
                disabled={!engineFile && !ybFile}
                variant="contained"
                size={'small'}
                sx={{
                  fontSize: '15px',
                  color: 'white',
                  background: '#3F51B5',
                  height: '42px',
                  width: '101px',
                }}
              >
                {t('statement_account_detail.save')}
              </LoadingButton>
            </Box>
          </form>
        </Paper>
      )}
    </ProtectedPage>
  )
}
